/**
 * **The spatial-query seam** — the *set-returning* sibling of the pairwise geometry ops
 * (`adjacent_to`, `range_to`). Where those ask "is `target` adjacent to `self`?" (a boolean
 * about a known pair), this
 * answers the inverted question an area effect needs: **"given shape Y at origin Z,
 * which combatants does it cover, filtered by X?"** — returning the *set* of entities a
 * template covers.
 *
 * **Why rules, not the port (`@lancer-vtt/space`):** geometry (`world.disk → Region<Cell>`
 * of cells) is the port's job, but "which *combatants* does this region cover" needs
 * combatant positions + factions from {@link EncounterState} — the domain. So the
 * intersect-and-filter lives here, next to the boolean answerer (which already imports the
 * space port and reads footprints via `worldFor`). The geometry is grid-agnostic for free:
 * a Blast 2 is `disk(center, 2)` whether the encounter is hex or square.
 *
 * **Determinism:** the result is sorted by combatant **`id`** (lexicographic), with
 * explicitly zero game meaning — targets are independent, so order only decides *which
 * recorded die feeds which target* in the draw-and-record stream. `id` is persisted +
 * immutable (stable across replay) and imputes no initiative — never `state.order`.
 *
 * **Shapes:** `disk` (Blast/Burst, radial) plus the directional `line`/`cone` (the `World<P>`
 * port patterns, aimed from the resolved `origin` at a recorded `toward` cell). **Combatants
 * only** (a selector asking for inert objects/terrain resolves its character members and warns,
 * deferring objects to the zones/objects follow-on). The seam is pure given `state` + the
 * recorded `site`/`toward` — it reads frozen state and never mutates, so the enumerated set
 * bakes into the per-target steps at resolve and replay never re-queries.
 */

import {
  type Cell,
  type Combatant,
  type EncounterState,
  hasStatus,
  isActor,
  isCharacter,
  sideOf,
  worldFor,
} from "@lancer-vtt/domain";
import type { AreaSelector, SelectKind } from "./atoms";
import { footprintCells } from "./spatialAnswerer";
import { evaluatePredicate, type PredicateContext } from "./predicate";
import type { ChoiceSource } from "./choices";
import type { LedgerCollision } from "./resolutionLedger";
import { trampledCharacterIds } from "../trample";

/** The bound actor ids an area query resolves a symbolic origin / faction against. */
export type AreaActors = {
  self?: string;
  target?: string;
  attacker?: string;
};

export type EnumerateAreaArgs = {
  state: EncounterState;
  selector: AreaSelector;
  actors: AreaActors;
  /** The recorded template-center {@link Cell} for `origin: "site"` (draw-and-record). */
  site?: Cell;
  /**
   * The recorded **aim direction** {@link Cell} for a directional `line`/`cone` selector — the
   * point the template points at from its apex (the resolved `origin`). Ignored by `disk`.
   * Recorded draw-and-record; never re-derived at playback (the enumerated set is baked into
   * the per-target steps).
   */
  toward?: Cell;
  /** Where a `gm_ruling` in the selector's `where` predicate reads its answer. */
  rulings?: ChoiceSource;
  /** The path this resolution's move walked, for a `passed_through` selector (`EffectContext.movePath`). */
  movePath?: MovePath;
  /** This resolution's push collisions, for a `collided` selector (`ResolutionLedger.collisions`). */
  collisions?: readonly LedgerCollision[];
};

/** A walk recorded mid-resolution: who moved and the anchors it occupied, `from` and `to` inclusive. */
export type MovePath = {
  mover: string;
  path: Cell[];
  /** The walk's atom authored `provokesReactions` (Impale) — the referee halts the activation for the move checkpoint first. */
  provokesReactions?: true;
  /** The walk's atom authored `involuntary` (Puppet System): its move event is not voluntary. */
  voluntary?: false;
};

/** The enumerated set + the honest advisory warnings (rejected shapes, deferred objects). */
export type EnumerateAreaResult = {
  /** The covered combatants, filtered by `select`/`where`, sorted by `id`. */
  combatants: Combatant[];
  /** Advisory notes (a rejected line/cone shape, an unresolvable origin, deferred objects). */
  warnings: string[];
};

/**
 * Whether a candidate matches the `select` faction/class filter, given the activating
 * `self` (for ally/enemy side derivation). `object` and `character_or_object` reach the board's
 * objects (`isObject`: placed cover, deployables, mines) that aren't Destroyed — the Thermal
 * Grenade's "objects … are hit automatically". Terrain cells hold no combatant and never match.
 */
function matchesSelect(candidate: Combatant, self: Combatant | undefined, select: SelectKind): boolean {
  if (!isCharacter(candidate)) return (select === "object" || select === "character_or_object") && !hasStatus(candidate.statuses, "Destroyed");
  switch (select) {
    case "character":
    case "character_or_object":
      return true;
    case "object":
      return false; // characters never satisfy an objects-only selector.
    case "drone":
      return !isActor(candidate); // a drone is the one non-actor character.
    case "ally":
      return self !== undefined && sideOf(candidate) === sideOf(self);
    case "enemy":
      return self !== undefined && sideOf(candidate) !== sideOf(self);
  }
}

/** The spaces of `self`'s placed, not Destroyed deployed drones (`origin: "own_drones"`). */
function ownDroneCells(state: EncounterState, selfId: string | undefined): Cell[] {
  if (selfId === undefined) return [];
  return Object.values(state.combatants)
    .filter((c) => c.deployedFrom?.combatantId === selfId && isCharacter(c) && !isActor(c) && !hasStatus(c.statuses, "Destroyed"))
    .flatMap((c) => (c.pos != null ? [c.pos] : []));
}

/** Resolve the template-origin cell from the selector's symbolic `origin` ref. */
function resolveOrigin(args: EnumerateAreaArgs): Cell | undefined {
  const { state, selector, actors, site } = args;
  if (selector.origin === "site") return site;
  if (selector.origin === "own_drones") return undefined;
  const id = selector.origin === "self" ? actors.self : actors.target;
  return id ? state.combatants[id]?.pos ?? undefined : undefined;
}

/**
 * Enumerate the combatants an {@link AreaSelector} covers. Resolves the
 * origin, builds the template `Region` via `world.disk`, intersects it against every placed
 * combatant's footprint, filters by `select` (+ optional `where`), applies `exclude_origin` and `exclude_target`,
 * and returns the set sorted by `id`. Pure; never mutates `state`.
 */
export function enumerateArea(args: EnumerateAreaArgs): EnumerateAreaResult {
  const { state, selector, actors, rulings, toward } = args;
  const warnings: string[] = [];

  // Directional shapes (line/cone) need a recorded aim direction.
  if ((selector.shape === "line" || selector.shape === "cone") && toward === undefined) {
    warnings.push(
      `for_each: shape "${selector.shape}" needs a recorded toward direction (none supplied) — no targets`,
    );
    return { combatants: [], warnings };
  }

  const world = worldFor(state);
  // `own_drones` is a union of disks, one per drone. Membership is "in any of them", so a character
  // in two is one member.
  const droneDisks = selector.origin === "own_drones" ? ownDroneCells(state, actors.self).map((at) => world.disk(at, selector.size)) : undefined;
  const origin = resolveOrigin(args);
  if (origin === undefined && droneDisks === undefined) {
    warnings.push(
      `for_each: could not resolve origin "${selector.origin}" (no recorded site / unplaced combatant) — no targets`,
    );
    return { combatants: [], warnings };
  }

  // The template region: `disk` (Blast/Burst) is radial; `line`/`cone` are directional off
  // the grid-agnostic port patterns, aimed from `origin` at `toward`.
  const region =
    origin === undefined
      ? undefined
      : selector.shape === "line"
        ? world.linePattern(origin, toward!, selector.size)
        : selector.shape === "cone"
          ? world.conePattern(origin, toward!, selector.size)
          : world.disk(origin, selector.size);
  const covers = (cell: Cell): boolean => (droneDisks !== undefined ? droneDisks.some((d) => d.contains(cell)) : region?.contains(cell) === true);
  const self = actors.self ? state.combatants[actors.self] : undefined;
  // The combatant to drop for `exclude_origin` (Burst): the symbolic origin's combatant.
  // A free-point `site` origin has no origin combatant, so exclude_origin is a no-op there.
  const originId = selector.origin === "self" ? actors.self : selector.origin === "target" ? actors.target : undefined;

  // `passed_through`: the characters the recorded move crossed, unioned with the template — the
  // SAME sweep Trample fires on (`trample.ts`), so "passed through" has one definition. `collided`
  // unions in the characters a push drove `target` into the same way.
  const passed = new Set(
    selector.passed_through && args.movePath !== undefined
      ? trampledCharacterIds({ state, moverId: args.movePath.mover, path: args.movePath.path })
      : [],
  );
  if (selector.collided) {
    for (const c of args.collisions ?? []) if (c.mover === actors.target) for (const id of c.occupants) passed.add(id);
  }

  // `first_reached`: the line's spaces walked out from the origin (its own footprint left out), and
  // the step at which each candidate is first on or beside one.
  const walk =
    selector.first_reached && selector.shape === "line" && region !== undefined && origin !== undefined
      ? region
          .cells()
          .filter((cell) => !(self?.pos && footprintCells(world, self).some((s) => world.equals(s, cell))))
          .sort((a, b) => world.distance(origin, a) - world.distance(origin, b))
      : undefined;
  const reachedAt = new Map<string, number>();

  const hits: Combatant[] = [];
  // Walk in id order so membership + the per-target draw order are deterministic.
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id]!;
    if (selector.exclude_origin && originId !== undefined && id === originId) continue;
    if (selector.exclude_target && id === actors.target) continue;
    const fp = footprintCells(world, c);
    if (fp.length === 0) continue; // unplaced — not on the map, not covered.
    if (walk !== undefined) {
      const step = walk.findIndex((at) => fp.some((cell) => world.distance(cell, at) <= 1));
      if (step < 0) continue; // the line never comes on or beside it.
      reachedAt.set(id, step);
    } else if (!passed.has(id) && !fp.some(covers)) continue; // template doesn't cover it.
    if (!matchesSelect(c, self, selector.select)) continue;
    if (selector.where !== undefined) {
      // The `where` predicate is evaluated with the candidate bound as `target`, so
      // has_status/stat/resource filter set members for free.
      const pctx: PredicateContext = {
        state,
        actors: { self: actors.self, attacker: actors.attacker ?? actors.self, target: id },
        ...(rulings !== undefined ? { rulings } : {}),
      };
      if (!evaluatePredicate(selector.where, pctx)) continue;
    }
    hits.push(c);
  }

  if (walk !== undefined && hits.length > 0) {
    const first = Math.min(...hits.map((c) => reachedAt.get(c.id)!));
    return { combatants: hits.filter((c) => reachedAt.get(c.id) === first), warnings };
  }
  return { combatants: hits, warnings };
}
